import * as React from 'react';

/**
 * Disclosure frame for the workspace's INFORMATIONAL tier — content that is useful context but not
 * a decision surface (campaign context, Zoho notes, research results, entity lookups). Collapses to
 * a one-line summary row so the actionable surfaces (task chooser, commit cards, questions) stay
 * front and center; expands in place. Visually the quiet sibling of the commit-card receipt.
 */
export function InfoPanelFrame({
  title,
  detail,
  defaultOpen,
  children
}: {
  title: string;
  /** Key facts shown in the collapsed row so the panel rarely needs opening. */
  detail?: string;
  /** Agent-emitted cards (content the user asked for) start open; ambient context stays closed.
   * Initial only — remount (key on the emitting part's id) to re-open for a NEW card. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  // Controlled open state: a bare `open` attribute would be re-asserted on every render and
  // clobber the user's toggle while the surrounding workspace re-renders mid-stream.
  const [open, setOpen] = React.useState(defaultOpen ?? false);
  return (
    <details
      open={open}
      onToggle={e => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <summary className="flex cursor-pointer select-none items-center gap-2 px-4 py-2.5 text-sm">
        <span className="text-xs text-gray-400" aria-hidden>
          {open ? '▾' : '▸'}
        </span>
        <span className="font-medium text-gray-900">{title}</span>
        {detail ? <span className="ml-auto text-xs text-gray-500">{detail}</span> : null}
      </summary>
      <div className="border-t p-4">{children}</div>
    </details>
  );
}
